import React from 'react';
import { isProductionServer } from '../firebase';
import { useLocation } from 'react-router-dom';
import Bugsnag from '@bugsnag/js';
import { store, RootState } from '../redux/store';
import utils from './utils';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';

const debug = !isProductionServer;
//const enabled = debug || isProductionServer;
const enabled = process.env.REACT_APP_SEGMENT_KEY;

declare global {
    interface Window {
        analytics: any;
    }
}

function getUserId(): string | undefined {
    const state: RootState = store.getState();

    if (state.userDetail && state.userDetail.id) {
        return state.userDetail.id;
    }

    const localUser = utils.getLocalUser();
    if (localUser && localUser.id) {
        return localUser.id;
    }

    return undefined;
}

function checkAndAddUserIdToParams(eventParams: any): any {
    let params = { ...eventParams };
    if (params.userId) {
        return params;
    }
    const userId = getUserId();
    if (userId) {
        params.userId = userId;
    }
    console.log(`analytics.getUserId=${userId}`);
    // if (!userId) {
    //     utils.printCallStack()
    // }
    return params;
}

async function checkAndAddStoreInfoToParams(eventParams: any): Promise<any> {
    const params = { ...eventParams };
    if (!utils.isAppInWidgetMode()) {
        return params;
    }
    await waitForStoreReady();
    const globalState = store.getState();
    if (!params.sourceUrl) {
        params.sourceUrl = globalState.widget.sourceUrl;
    }
    if (!globalState.widget.store) {
        return params;
    }
    params.storeInfo = pick(globalState.widget.store, [
        'id',
        'taffiId',
        'name',
        'url',
        'shopifyDomain',
        'platform',
        'defaultPagePath',
        'enableCustomWidgetScript',
        'enableShopifyItemSearch',
        'productTargetGender',
        'tagsToIdentifyMan',
        'tagsToIdentifyWoman',
        'supportedLanguages',
        'isTestStore',
    ]);
    return params;
}

async function checkAndAddCommonInfoToParams(params: any, addUserInfo: boolean = true): Promise<any> {
    try {
        // call checkAndAddStoreInfoToParams before
        // checkAndAddUserIdToParams to ensure store is ready
        params = await checkAndAddStoreInfoToParams(params);
        if (addUserInfo) {
            params = checkAndAddUserIdToParams(params);
        }
        return params;
    } catch (error) {
        console.error('checkAndAddUserAndStoreInfoToParams:error=', error);
        return params;
    }
}

function refreshAnalyticId() {
    window.analytics.reset();
}

function usePageViews() {
    const location = useLocation();
    React.useEffect(() => {
        if (!enabled) {
            console.debug('INFO: segment analytics is not enabled');
        } else {
            const trackPageViews = async () => {
                if (utils.isAppInWidgetMode() && !utils.isWidgetOpened()) {
                    return;
                }
                let params: any = {
                    buildVersion: process.env.REACT_APP_WEBAPP_VERSION || 'development',
                };
                params = await checkAndAddCommonInfoToParams(params);
                window.analytics.page(location.pathname, location.pathname, params);
            };
            try {
                trackPageViews();
            } catch (error) {
                console.error('usePageViews:error=', error);
            }
        }
    }, [location]);
}

async function identify(uid, params) {
    Bugsnag.setUser(params.userId as string, params.email as string);
    if (debug) {
        console.debug('trackEvent: identify=>', params);
    }
    if (!enabled) {
        return;
    }
    params = await checkAndAddCommonInfoToParams(params, false);
    window.analytics.identify(uid, params);
}

function waitForStoreReady(): Promise<void> {
    return new Promise((resolve, reject) => {
        const maxRetries = 300; // 30 seconds / 100ms = 300 retries
        let retries = 0;

        const interval = setInterval(() => {
            const state: RootState = store.getState();
            retries++;
            if (!isEmpty(state.widget.store)) {
                clearInterval(interval);
                console.log('%c StoreInfo is ready', 'color: yellow');
                resolve();
            } else if (retries >= maxRetries) {
                clearInterval(interval);
                reject(new Error('Timeout: Store is still empty after 30 seconds'));
            }
        }, 100); // 100ms interval
    });
}

async function trackEvent(eventName, eventParams) {
    if (debug) {
        console.debug('trackEvent:' + eventName, '=>', eventParams);
    }
    if (!enabled) {
        return;
    }

    //let params = { ...eventParams };
    let params = await checkAndAddCommonInfoToParams(eventParams);
    window.analytics.track(eventName, {
        ...params,
        buildVersion: process.env.REACT_APP_WEBAPP_VERSION || 'development',
    });
}

async function trackEventUsingBeacon(eventName, eventParams) {
    if (debug) {
        console.debug('trackEvent:' + eventName, '=>', eventParams);
    }
    if (!enabled) {
        return;
    }

    const url = 'https://api.segment.io/v1/t';
    const payload = JSON.stringify({
        anonymousId: window?.analytics?.user()?.anonymousId(),
        userId: eventParams?.userId,
        event: eventName,
        properties: {
            ...eventParams,
            buildVersion: process.env.REACT_APP_WEBAPP_VERSION || 'development',
        },
        writeKey: process.env.REACT_APP_SEGMENT_KEY,
    });
    navigator.sendBeacon(url, payload);
}

export const TRACK_EVENT = {
    SIGNUP: 'signup',
    LOGOUT: 'logout',

    WHATSAPP_CONTACT: 'whatsapp_contact',

    PRODUCT_SEARCH: 'product_search',
    PRODUCT_RECOMMENDATION: 'product_recommendation',
    PRODUCT_VIEW: 'product_view',
    STYLE_STITCHER_PRODUCT_DISPLAYED: 'stylestitcher_product_displayed',
    SHOP_THE_PRODUCT: 'shop_the_product',
    //PRODUCT_REACTION: "product_reaction",
    STOCK_ACTION: 'stock_action',
    GRAPHQL_ERRORS: 'fe_graphql_errors',
    NETWORK_ERROR: 'fe_network_errors',
    BUSINESS_ERROR: 'fe_business_error',
    // Profile events
    PROFILE_PICTURE_UPDATE: 'profile_picture_update',

    //STYLIST_CONSULT_REQUEST: "stylist_consult_request",
    JOIN_STYLING_SESSION_STYLIST: 'join_zoom_session_stylist',
    JOIN_STYLING_SESSION_CUSTOMER: 'join_zoom_session_customer',
    DISCONNECT_CALENDLY: 'disconnect_calendly',
    CONNECT_CALENDLY: 'connect_calendly',

    // Invoice events
    INVOICE_DOWNLOAD: 'invoice_download',
    STYLIST_CONNECT_PROMPT: 'stylist_connect_prompt',

    PRODUCT_PERSONALIZED: 'product_personalized',

    PRODUCT_INFO_OPEN: 'product_info_open',
    PRODUCT_INFO_CLOSE: 'product_info_close',
    ENLARGE_PRODUCT_IMAGE_OPEN: 'enlarge_product_image_open',
    ENLARGE_PRODUCT_IMAGE_CLOSE: 'enlarge_product_image_close',
    USER_PHOTO_SKIP: 'user_photo_skip',
    USER_SOCIAL_MEDIA_SKIP: 'user_social_media_skip',
    VIEW_STYLING_SESSION_OUTCOME: 'view_styling_session_outcome',
    // Stylist search
    STYLIST_SEARCH: 'stylist_search',

    PLAY_INTRODUCING_VIDEO_ON_HOMEPAGE: 'play_introducing_video_on_homepage',
    PLAY_INTRODUCING_VIDEO_ON_SIGNUP: 'play_introducing_video_on_signup',

    // Release Features
    RELEASE_FEATURES: 'release_features',

    //Quiz
    USER_QUIZ_START: 'user_quiz_start',
    USER_QUIZ_UPDATE: 'user_quiz_update',
    USER_QUIZ_COMPLETE: 'user_quiz_complete',
    USER_QUIZ_HELP_OPEN: 'user_quiz_help_open',
    USER_QUIZ_GLOBAL_SKIP_CLICKED: 'user_quiz_global_skip_clicked',

    // Widget actions
    WIDGET_OPENED: 'widget_opened',
    WIDGET_CLOSED: 'widget_closed',
    TAFFI_WIDGET_POPUP_DISPLAYED: 'taffi_widget_popup_displayed',
    TAFFI_WIDGET_POPUP_USER_CLOSED: 'taffi_widget_popup_user_closed',
    TAFFI_WIDGET_POPUP_QUESTION_CLICKED: 'taffi_widget_popup_question_clicked',

    //style-chat
    STYLE_CHAT_PRODUCT_CLICKED: 'style_chat_product_clicked',
    STYLE_CHAT_ENGAGING_STYLES_CLICKED: 'style_chat_engaging_styles_clicked',
    BUSY_INDICATOR_WITH_STYLES: 'busy_indicator_with_styles',
    STYLE_CHAT_CONNECT_TO_STYLIST: 'style_chat_connect_to_stylist',
    STYLE_CHAT_PRODUCT_IMAGE_CLICKED: 'style_chat_product_image_clicked',
    STYLE_CHAT_PRODUCT_DISPLAYED: 'style_chat_product_displayed',
    STYLE_CHAT_SHOW_MORE_STYLES_CLICKED: 'style_chat_show_more_styles_clicked',
    STYLE_CHAT_TRENDING_QUESTION_CLICKED: 'style_chat_trending_question_clicked',
    STYLE_CHAT_ACCESS_OPTION_CLICKED: 'style_chat_access_option_clicked',
    STYLE_CHAT_GENDER_MISMATCH: 'style_chat_gender_mismatch',
    STYLE_CHAT_DESCRIPTION_ACTION: 'style_chat_description_action',
    STYLE_CHAT_STYLIST_VIEW: 'style_chat_stylist_view',
    STYLE_CHAT_CREATE_PROFILE_POPUP: 'style_chat_create_profile_popup',
    TAFFI_WIDGET_LOADED: 'taffi_widget_loaded',
    TAFFI_WIDGET_NOT_DISPLAYED: 'taffi_widget_not_displayed',
    STYLE_LISTING_PRODUCT_URLS: 'style_listing_product_urls',
    STYLE_CHAT_OTHER_OPTION_CLICKED: 'style_chat_other_option_clicked',
    STYLE_CHAT_QUIZ_POPUP_BUTTON_DISPLAYED: 'style_chat_quiz_popup_button_displayed',
    STYLE_CHAT_QUIZ_POPUP_BUTTON_CLICKED: 'style_chat_quiz_popup_button_clicked',
    STYLE_CHAT_QUIZ_HANGER_BUTTON_CLICKED: 'style_chat_quiz_hanger_button_clicked',
    STYLE_CHAT_QUIZ_POPUP_BUTTON_CLOSED: 'style_chat_quiz_popup_button_closed',
    REAL_TIME_DATABASE_CONNECTION: 'real_time_database_connection',
    STYLE_CHAT_FEEDBACK_NO_CLICKED: 'style_chat_feedback_no_clicked',
    STYLE_CHAT_FEEDBACK_YES_CLICKED: 'style_chat_feedback_yes_clicked',

    // Feedback
    FEEDBACK_OPEN: 'feedback_open',
    FEEDBACK_VIEW: 'feedback_view',

    STYLE_QUIZ_QUESTION_PENDING: 'style_quiz_question_pending',

    SIGNUP_VIEW_LOADED: 'signup_view_loaded',
    // REVIEW A STYLE
    FIX_THE_STYLE_CLICKED: 'stylestitcher_fix_the_style_clicked',
    CANNOT_BE_FIXED_CLICKED: 'stylestitcher_cannot_be_fixed_clicked',
    SOCIAL_AUTH_UNSUPPORTED_POPUP: 'social_auth_unsupported_popup',
    HOME_PAGE_AMIRA_BANNER_CLICKED: 'home_page_amira_banner_clicked',
    // BUGSNAG
    BUGSNAG_ERROR_CAUGHT: 'bugsnag_error_caught',
    BUGSNAG_ERROR_FALLBACK: 'bugsnag_error_fallback',
    HANDLE_CHUNK_LOADING_FAIL: 'handle_chunk_loading_fail',
    // style widget
    STYLE_IT_RIGHT_STYLE_CLICKED: 'style_it_right_style_clicked',
    DRESS_IT_RIGHT_VIEWED: 'dress_it_right_viewed',
    STYLE_WIDGET_AMIRA_CLICKED: 'style_widget_amira_clicked',
    TAFFI_WIDGET_LISTING_STYLE_CLICKED: 'taffi_widget_listing_style_clicked',
    PLP_PLACEHOLDER_PRODUCT_VIEW: 'plp_placeholder_product_view',
    PLP_PLACEHOLDER_PRODUCT_CLICKED: 'plp_placeholder_product_clicked',
    TAFFI_PDP_WIDGET_INTERACTION: 'taffi_pdp_widget_interaction',
    STYLE_CHAT_PRODUCT_QUESTION_CLICKED: 'style_chat_product_question_clicked',
    STYLE_CHAT_PRODUCT_ANSWER_REACTION: 'style_chat_product_answer_reaction',
    TAFFI_PLP_WIDGET_ICON_CLICK: 'taffi_plp_widget_icon_clicked',
    TAFFI_PLP_HANGER_ICON_DISPLAYED: 'taffi_plp_hanger_icon_displayed',
    CHECK_PENDING_TAFFI_PLP_HANGER_ICON_DISPLAYED: 'check_pending_taffi_plp_hanger_icon_displayed',
    STYLE_CHAT_PRODUCT_RECOMMENDATION_CLICKED: 'style_chat_product_recommendation_clicked',
    // search widget
    SEARCH_WIDGET_QUESTION_CLICKED: 'search_widget_question_clicked',
    SEARCH_WIDGET_STYLE_CLICKED: 'search_widget_style_clicked',
    SEARCH_WIDGET_STYLES_RECOMMENDED: 'search_widget_styles_recommended',
    // trigger GA event when PDP widget is shown
    GA_PDP_WIDGET_SHOWN: 'ga_pdp_widget_shown',
    // VTON features
    VTON_SCREEN_VIEWED: 'vton_screen_viewed',
    VTON_MODEL_UPLOADED: 'vton_model_uploaded',
};

// post message events sent to parent window for Google Analytics tracking (handled by the parent window - the store)
export enum GA_POST_MESSAGE_EVENT {
    TAFFI_WIDGET_SHOWN = 'taffi_widget_shown',
    TAFFI_WIDGET_ENGAGED = 'taffi_widget_engaged',
    TAFFI_WIDGET_UNAVAILABLE = 'taffi_widget_unavailable',
}

export enum GA_ENGAGEMENT_TYPE {
    CAROUSEL_NEXT = 'carousel_next',
    CAROUSEL_PREV = 'carousel_prev',
    POPUP_OPENED = 'popup_opened',
    SHOP_THE_PRODUCT = 'shop_the_product',
    SHOW_STYLE = 'show_style',
    SHOW_ITEM = 'show_item',
    GO_TO_PDP = 'go_to_pdp',
}

export enum WHATSAPP_REASON_TYPE {
    PRODUCT_SEARCH_ZERO = 'product_search_zero',
    PRODUCT_RECOMMENDATION_ZERO = 'product_recommendation_zero',
    PRODUCT_LAST_RESULT = 'product_last_result',
    PRODUCT_QUESTION = 'product_question',
    PRODUCT_NOT_FOUND = 'product_not_found',
    STYLE_GUIDE_REQUEST = 'style_guide_request',
    STYLIST_CONNECT_REQUEST = 'stylist_connect_request',
    STYLIST_CONSULT_REQUEST = 'stylist_consult_request',
}

export enum REACTION_TYPE {
    LIKE = 'like',
    DISLIKE = 'dislike',
}

export enum PRODUCT_INTERACTION_TYPE {
    VIEW = 'view',
}

export enum PRODUCT_TYPE {
    STYLE = 'style',
    ITEM = 'item',
}

export enum STOCK_ACTION_TYPE {
    MODIFY_CART = 'modify_cart',
    CONTINUE_CHECKOUT = 'continue_checkout',
}

export enum STYLIST_CONNECT_PROMPT_ACTION {
    MAYBE_LATER = 'maybe_later',
    ASK_AMIRA_NOW = 'ask_amira_now',
}

export enum IMAGE_POPUP_TEXT_ACTION {
    MORE = 'more',
}

export enum USER_AUTH_STATE {
    AUTHENTICATED = 'authenticated',
    GUEST = 'guest',
}

export { usePageViews, identify, trackEvent, refreshAnalyticId, trackEventUsingBeacon };
